import Duck from 'extensible-duck';
import Immutable from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';
import { schema, denormalize, normalize } from 'normalizr';
import { green500, yellow800, red500 } from 'material-ui/styles/colors';
import isObject from 'lodash/isObject';
import get from 'lodash/get';
import requestsManager from 'lib/requestsManagerAxios'; // eslint-disable-line
import { materializeToastErrorResponse } from '../../../../lib/functions/utils';
import { centsToCurrency } from 'appDir/lib/functions/convert';

const answerSchema = new schema.Entity('$$answers');
const registrantSchema = new schema.Entity('$$registrants', {
  answers: [answerSchema],
});

export default new Duck({
  namespace: 'organization-config',
  store: '$$organizerRegistrantsStore',
  types: [
    'SORT_TABLE',
    'SELECT_REGISTRANT',
    'CLOSE_SNACKBAR',
    'SUBMIT_REFUND',
    'SUBMIT_REFUND_PENDING',
    'SUBMIT_REFUND_REJECTED',
    'SUBMIT_REFUND_FULFILLED',
    'FETCH_REGISTRANTS',
    'FETCH_REGISTRANTS_REJECTED',
    'FETCH_REGISTRANTS_FULFILLED',
    'FETCH_REGISTRANTS_FINAL',
    'ARCHIVE_REGISTRANT',
    'ARCHIVE_REGISTRANT_PENDING',
    'ARCHIVE_REGISTRANT_REJECTED',
    'ARCHIVE_REGISTRANT_FULFILLED',
    'CHANGE_REGISTRANT',
    // 'MOVE_REGISTRANT',
    // 'MOVE_REGISTRANT_PENDING',
    // 'MOVE_REGISTRANT_REJECTED',
    // 'MOVE_REGISTRANT_FULFILLED',
    'UPDATE_INVOICE',
    'UPDATE_INVOICE_PENDING',
    'UPDATE_INVOICE_REJECTED',
    'UPDATE_INVOICE_FULFILLED',
    'UPDATE_REGISTRANT',
    'UPDATE_REGISTRANT_PENDING',
    'UPDATE_REGISTRANT_REJECTED',
    'UPDATE_REGISTRANT_FULFILLED',
  ],
  initialState: Immutable.fromJS({
    devToolsOn: true,
    setIsFetching: false,
    setIsSaving: false,
    snackbarMessage: '',
    snackbarOpen: false,
    orderBy: 'createdAt',
    order: 'desc',
    selectedRegistrantId: -1,
    $$entities: {
      $$registrants: {},
    },
    $$registrantIds: [],
    initialLoading: true,
    loadingProgress: 0,
  }),
  reducer: ($$state, { type, payload, meta }, { types }) => {
    switch (type) {
      case types.ARCHIVE_REGISTRANT_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.ARCHIVE_REGISTRANT_REJECTED:
        const snackbarMessage = payload.response.data
          ? get(payload.response.data, ['base', 0])
          : 'Something went wrong. Please refresh and try again';
        return $$state.merge({
          snackbarMessage,
          snackbarOpen: true,
          setIsSaving: false,
        });
      case types.ARCHIVE_REGISTRANT_FULFILLED:
        return $$state
          .update('$$registrantIds', $$registrantIds =>
            $$registrantIds.filterNot(
              registrantId => registrantId === meta.registrantId
            )
          )
          .merge({
            setIsSaving: false,
            snackbarMessage: 'Registrant has been deleted.',
            snackbarOpen: true,
          });
      case types.CHANGE_REGISTRANT:
        return $$state.mergeDeepIn(
          ['$$entities', '$$registrants', `${payload.id}`],
          payload.registrant
        );
      case types.CLOSE_SNACKBAR:
        return $$state.merge({
          snackbarOpen: false,
          snackbarMessage: '',
        });
      case types.FETCH_REGISTRANTS_REJECTED:
        return $$state.merge({
          snackbarOpen: true,
          snackbarMessage:
            "We're having issues fetching your registrants. Please try refreshing.",
        });
      case types.FETCH_REGISTRANTS_FULFILLED: {
        const normalizedRegistrants = normalize(payload.registrants, [
          registrantSchema,
        ]);
        return $$state
          .mergeDeep({
            $$entities: Immutable.fromJS(normalizedRegistrants.entities),
            initialLoading: false,
            loadingProgress: (payload.current_page / payload.total_pages) * 100,
          })
          .update('$$registrantIds', $$registrantIds =>
            $$registrantIds.concat(Immutable.List(normalizedRegistrants.result))
          );
      }
      case types.FETCH_REGISTRANTS_FINAL:
        return $$state.merge({ setIsFetching: false, loadingProgress: 100 });
      // case types.MOVE_REGISTRANT_PENDING:
      //   return $$state.merge({ setIsSaving: true });
      // case types.MOVE_REGISTRANT_REJECTED:
      //   return $$state.merge({
      //     setIsSaving: false,
      //     snackbarMessage: 'Malfunction! Contact Obi Wan Rattermann!',
      //     snackbarOpen: true,
      //   });
      // case types.MOVE_REGISTRANT_FULFILLED:
      //   return $$state
      //     .update('$$registrantIds', $$registrantIds =>
      //       $$registrantIds.filterNot(
      //         registrantId => registrantId === meta.registrantId
      //       )
      //     )
      //     .merge({
      //       setIsSaving: false,
      //       snackbarMessage: 'Participant has been moved.',
      //       snackbarOpen: true,
      //     });
      case types.SELECT_REGISTRANT:
        return $$state.merge({
          selectedRegistrantId:
            $$state.get('selectedRegistrantId') === payload.registrantId
              ? -1
              : payload.registrantId,
        });
      case types.SORT_TABLE:
        return $$state.merge({
          orderBy: payload.orderBy,
          order: payload.order,
        });
      case types.SUBMIT_REFUND_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.SUBMIT_REFUND_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.SUBMIT_REFUND_FULFILLED:
        const normalizedRegistrant = normalize(payload.data, registrantSchema);
        const { id } = payload.data;
        return $$state
          .setIn(
            ['$$entities', '$$registrants', `${id}`],
            Immutable.fromJS(normalizedRegistrant.entities.$$registrants[id])
          )
          .merge({
            setIsSaving: false,
            snackbarOpen: true,
            snackbarMessage: 'Refund has been issued.',
          });
      case types.UPDATE_INVOICE_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_INVOICE_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.UPDATE_INVOICE_FULFILLED: {
        const invoiceIndex = $$state
          .getIn([
            '$$entities',
            '$$registrants',
            `${payload.data.registration_registrant_id}`,
            'payment_plan_invoices',
          ])
          .findIndex(invoice => invoice.get('id') === payload.data.id);
        Materialize.toast('Saved', 3000);
        return $$state
          .merge({ setIsSaving: false })
          .setIn(
            [
              '$$entities',
              '$$registrants',
              `${payload.data.registration_registrant_id}`,
              'payment_plan_invoices',
              invoiceIndex,
            ],
            Immutable.fromJS(payload.data)
          );
      }
      case types.UPDATE_REGISTRANT_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_REGISTRANT_REJECTED:
        materializeToastErrorResponse(payload.response.data);
        return $$state.merge({ setIsSaving: false });
      case types.UPDATE_REGISTRANT_FULFILLED:
        Materialize.toast('Saved', 3000);
        return $$state
          .mergeDeepIn(
            ['$$entities', '$$registrants', `${payload.data.id}`],
            payload.data
          )
          .merge({ setIsSaving: false });
      default:
        return $$state;
    }
  },
  selectors: {
    order: $$state => $$state.get('order'),
    orderBy: $$state => $$state.get('orderBy'),
    headerColumns: new Duck.Selector(_selectors =>
      createSelector(
        $$state => $$state.get('$$questions'),
        $$state => $$state.get('$$program'),
        ($$questions, $$program) => {
          const headerColumns = [
            { id: 'name', label: 'Participant Name' },
            { id: 'createdAt', label: 'Date Registered' },
            { id: 'amountPaid', label: 'Amount Paid' },
            { id: 'amountDonated', label: 'Amount Donated' },
            { id: 'amountRefunded', label: 'Amount Refunded' },
            { id: 'note', label: 'Notes' },
          ];

          if ($$program.get('type_of_registrant') === 'player') {
            headerColumns.push({ id: 'dob', label: 'Date of Birth' });
            headerColumns.push({ id: 'gender', label: 'Gender' });
          }

          const programColumns = $$questions
            .map($$question => ({
              id: `question-${$$question.get('id')}`,
              label: $$question.get('question_text'),
              numeric: false,
              disablePadding: false,
              question: true,
            }))
            .toJS();
          return headerColumns.concat(programColumns);
        }
      )
    ),
    programs: new Duck.Selector(_selectors =>
      createSelector(
        $$state => $$state.get('$$programs'),
        $$state => $$state.get('$$program'),
        ($$programs, $$program) =>
          $$programs
            .filterNot(
              $$programFromPrograms =>
                $$programFromPrograms.get('id') === $$program.get('id')
            )
            .toJS()
      )
    ),
    $$registrants: new Duck.Selector(selectors =>
      createSelector(
        $$state => $$state.get('$$entities'),
        $$state => $$state.get('$$registrantIds'),
        $$state => $$state.get('$$program'),
        selectors.orderBy,
        selectors.order,
        ($$entities, $$registrantIds, $$program, orderBy, order) => {
          const answerSchema = new schema.Entity('$$answers');
          const registrantSchema = new schema.Entity('$$registrants', {
            answers: [answerSchema],
          });

          const denormalized = denormalize(
            $$registrantIds.toSet().toList().toJS(),
            [registrantSchema],
            $$entities.toJS()
          );

          // const currency = $$program.get('currency_code');

          return Immutable.fromJS(denormalized)
            .map($$registrant => {
              let amountPaid;
              let amountRefunded;
              let amountDonated = $$registrant.get('donation_amount');
              const currency =
                $$registrant.getIn(['initial_charge', 'currency']) || 'USD';
              if ($$registrant.get('added_manually')) {
                amountPaid = 'Added Manually';
                amountRefunded = '';
              } else if ($$registrant.get('payment_plan')) {
                // get everything they've paid
                let sumCents =
                  $$registrant.getIn(['initial_charge', 'amount_cents']) || 0;
                let sumRefundedCents =
                  $$registrant.getIn([
                    'initial_charge',
                    'amount_refunded_cents',
                  ]) || 0;
                let paidAllInvoices = true;
                let atLeastOneFailed = false;
                $$registrant
                  .getIn(['payment_plan_invoices'])
                  .forEach($$invoice => {
                    sumCents +=
                      $$invoice.getIn(['charge', 'amount_cents']) || 0;
                    sumRefundedCents +=
                      $$invoice.getIn(['charge', 'amount_refunded_cents']) || 0;
                    if ($$invoice.get('status') !== 'paid')
                      paidAllInvoices = false;
                    if ($$invoice.get('status') === 'failed')
                      atLeastOneFailed = true;
                  });

                let color;
                let tooltip;
                if (atLeastOneFailed) {
                  color = red500;
                  tooltip = 'A payment has failed';
                } else if (paidAllInvoices) {
                  color = green500;
                  tooltip = 'All payments processed successfully';
                } else {
                  color = yellow800;
                  tooltip = 'Participant has pending payments';
                }
                amountPaid = {
                  amount: centsToCurrency(sumCents, currency, true),
                  color,
                  tooltip,
                };
                amountRefunded =
                  sumRefundedCents === 0
                    ? ''
                    : centsToCurrency(sumRefundedCents, currency, true);
              } else {
                const amountPaidCents =
                  $$registrant.getIn(['initial_charge', 'amount_cents']) || 0;
                amountPaid = centsToCurrency(amountPaidCents, currency, true);
                const amountRefundedCents =
                  $$registrant.getIn([
                    'initial_charge',
                    'amount_refunded_cents',
                  ]) || 0;
                amountRefunded =
                  amountRefundedCents === 0
                    ? ''
                    : centsToCurrency(amountRefundedCents, currency, true);
              }
              const dob = $$registrant.get('dob')
                ? moment($$registrant.get('dob')).format('MMM D, YYYY')
                : null;

              const maxRefundCents =
                $$registrant.getIn(['initial_charge', 'amount_cents']) -
                $$registrant.getIn(['initial_charge', 'amount_refunded_cents']);

              let formatted = {
                id: $$registrant.get('id'),
                name: `${$$registrant.get('last_name')}, ${$$registrant.get(
                  'first_name'
                )}`,
                createdAt: moment($$registrant.get('created_at')).format(
                  'MMM D, YYYY'
                ),
                amountPaid,
                amountRefunded,
                amountDonated,
                dob,
                gender: $$registrant.get('gender'),
                maxRefund: centsToCurrency(maxRefundCents, currency, true),
                note: $$registrant.get('note'),
              };

              $$registrant.get('answers').forEach($$answer => {
                formatted = Object.assign(
                  {
                    [`question-${$$answer.get(
                      'registration_custom_question_id'
                    )}`]: $$answer.get('answer_string'),
                  },
                  formatted
                );
              });

              return $$registrant.set('formatted', formatted);
            })
            .sort(($$a, $$b) => {
              let a = $$a.get('formatted')[orderBy] || '';
              let b = $$b.get('formatted')[orderBy] || '';
              a = isObject(a) ? a.amount : a;
              b = isObject(b) ? b.amount : b;
              return order === 'desc' ? b.localeCompare(a) : a.localeCompare(b);
            });
        }
      )
    ),
    $$registrantsSorted: new Duck.Selector(selectors =>
      createSelector(
        selectors.$$registrants,
        $$state => $$state.get('orderBy'),
        $$state => $$state.get('order'),
        ($$registrants, orderBy, order) => {
          switch (orderBy) {
            case 'amountRefunded':
              return $$registrants.sort(($$a, $$b) => {
                const a = $$a.get('amount_refunded');
                const b = $$b.get('amount_refunded');
                return order === 'desc' ? b - a : a - b;
              });
            case 'amountPaid':
              return $$registrants.sort(($$a, $$b) => {
                const a = $$a.get('amount_cents');
                const b = $$b.get('amount_cents');
                return order === 'desc' ? b - a : a - b;
              });
            case 'dob':
              return $$registrants.sort(($$a, $$b) => {
                const a = moment($$a.get('dob')).unix();
                const b = moment($$b.get('dob')).unix();
                return order === 'desc' ? b - a : a - b;
              });
            case 'createdAt':
              return $$registrants.sort(($$a, $$b) => {
                const a = moment($$a.get('created_at')).unix();
                const b = moment($$b.get('created_at')).unix();
                return order === 'desc' ? b - a : a - b;
              });
            default:
              return $$registrants.sort(($$a, $$b) => {
                const a = $$a.get('formatted')[orderBy] || '';
                const b = $$b.get('formatted')[orderBy] || '';
                return order === 'desc'
                  ? b.localeCompare(a)
                  : a.localeCompare(b);
              });
          }
        }
      )
    ),
    registrants: new Duck.Selector(selectors =>
      createSelector(
        selectors.headerColumns,
        selectors.$$registrantsSorted,
        (headerColumns, $$registrantsSorted) => {
          return $$registrantsSorted
            .map($$registrant =>
              $$registrant.set(
                'row',
                headerColumns.map(
                  headerColumn => $$registrant.get('formatted')[headerColumn.id]
                ) || ''
              )
            )
            .toJS();
        }
      )
    ),
  },
  creators: duck => ({
    archiveRegistrant: registrantId => ({
      type: duck.types.ARCHIVE_REGISTRANT,
      payload: requestsManager.delete(`/admin/registrants/${registrantId}`),
      meta: { registrantId },
    }),
    changeRegistrant: (id, registrant) => ({
      type: duck.types.CHANGE_REGISTRANT,
      payload: { id, registrant },
    }),
    closeSnackbar: () => ({ type: duck.types.CLOSE_SNACKBAR }),
    fetchRegistrants: (page = 1) => (dispatch, getState) => {
      const $$state = getState().$$organizerRegistrantsStore;
      const programId = $$state.getIn(['$$program', 'id']);
      const perPage = 50;
      requestsManager
        .get(
          `/admin/programs/${programId}/registrants.json?page=${page}&per_page=${perPage}`
        )
        .then(res => {
          dispatch({
            type: duck.types.FETCH_REGISTRANTS_FULFILLED,
            payload: res.data,
          });
          if (res.data.current_page >= res.data.total_pages) {
            dispatch({ type: duck.types.FETCH_REGISTRANTS_FINAL });
          } else {
            const getCreators = () => duck.creators;
            const { fetchRegistrants } = getCreators();
            dispatch(fetchRegistrants(page + 1));
          }
        })
        .catch(_error => {
          dispatch({ type: duck.types.FETCH_REGISTRANTS_REJECTED });
        });
    },
    // moveRegistrant: (registrantId, registration_program_id) => ({
    //   type: duck.types.MOVE_REGISTRANT,
    //   payload: requestsManager.post(
    //     `/jwadmin/registrants/${registrantId}/move_to_program`,
    //     { registration_program_id }
    //   ),
    //   meta: { registrantId },
    // }),
    resendFailedNotice: invoice => ({
      type: duck.types.UPDATE_INVOICE,
      payload: requestsManager.post(
        `/admin/payment_plan_invoices/${invoice.id}/resend_failed_notice`
      ),
    }),
    selectRegistrant: registrantId => ({
      type: duck.types.SELECT_REGISTRANT,
      payload: { registrantId },
    }),
    sortTable: (orderBy, order) => ({
      type: duck.types.SORT_TABLE,
      payload: { orderBy, order },
    }),
    submitRefund: (stripeChargeId, refund_amount) => ({
      type: duck.types.SUBMIT_REFUND,
      payload: requestsManager.post(
        `/admin/stripe_charges/${stripeChargeId}/refund`,
        { refund_amount }
      ),
    }),
    updateInvoice: (id, invoice) => ({
      type: duck.types.UPDATE_INVOICE,
      payload: requestsManager.patch(`/admin/payment_plan_invoices/${id}`, {
        invoice,
      }),
    }),
    updateRegistrant: id => (dispatch, getState) => {
      const $$state = getState().$$organizerRegistrantsStore;
      const registrant = $$state
        .getIn(['$$entities', '$$registrants', `${id}`])
        .toJS();
      return dispatch({
        type: duck.types.UPDATE_REGISTRANT,
        payload: requestsManager.patch(`/admin/registrants/${id}`, {
          registrant,
        }),
      });
    },
  }),
});
